import React from "react";
import { View, Text, Image, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

function RichText({ text, color }) {
  return <Text style={[styles.paragraph, { color }]}>{String(text || "").split(/(\*\*[^*]+\*\*)/g).map((part, idx) =>
    part.startsWith("**") && part.endsWith("**")
      ? <Text key={idx} style={styles.emphasis}>{part.slice(2, -2)}</Text>
      : <Text key={idx}>{part}</Text>)}</Text>;
}

function TeachingBlock({ block, colors, isDarkMode }) {
  if (block.type === "teaching_illustration") {
    if (!String(block.preview_data_uri || "").startsWith("data:image/")) return null;
    return <View style={[styles.illustration, { borderColor: colors.border, backgroundColor: isDarkMode ? "#142131" : "#f8fafc" }]}>
      <Image source={{ uri: block.preview_data_uri }} style={styles.illustrationImage} resizeMode="contain" accessibilityLabel={block.alt || "Instructional illustration"} />
      <Text style={[styles.caption, { color: colors.muted }]}>{block.caption || "Original instructional illustration"}</Text>
    </View>;
  }
  // Existing PWO posters and video storyboards are *only* shown beside their
  // resource cards below, never inserted into the authored teaching narrative.
  if (!["concept_explanation", "key_idea"].includes(block.type)) return null;
  if (block.type === "key_idea") return <View style={[styles.idea, { backgroundColor: isDarkMode ? "#172744" : "#eff6ff" }]}>
    <View style={styles.ideaHeader}><MaterialCommunityIcons name="lightbulb-on-outline" size={17} color={colors.accent}/>
      <Text style={[styles.ideaLabel, { color: colors.accent }]}>KEY IDEA</Text></View>
    <RichText text={block.text} color={colors.text} />
  </View>;
  return <RichText text={block.text} color={colors.text} />;
}

function fallbackBlocks(section) {
  return (section.blocks || []).filter(b => ["explanation", "key_point"].includes(b.type)).map(b => ({
    id: b.block_id, type: b.type === "explanation" ? "concept_explanation" : "key_idea", text: b.text,
  }));
}

export default function AuthoredSoundLesson({ sections, colors, isDarkMode, topicLabel = "Sound" }) {
  if (!sections?.length) return null;
  return <View style={styles.shell}>
    <Text style={[styles.overline, { color: colors.muted }]}>{String(topicLabel || "Sound").toUpperCase()} · TEACHER PREVIEW</Text>
    {sections.map((section, idx) => <View key={section.section_id} style={[styles.lesson, { backgroundColor: colors.panel, borderColor: colors.border }]}>
      <Text style={[styles.number, { color: colors.accent }]}>CONCEPT {idx + 1}</Text>
      <Text style={[styles.title, { color: colors.text }]}>{section.title}</Text>
      {(Array.isArray(section.learning_blocks) && section.learning_blocks.length ? section.learning_blocks : fallbackBlocks(section))
        .map((block, index) => <TeachingBlock key={block.id || `b-${index}`} block={block} colors={colors} isDarkMode={isDarkMode} />)}
    </View>)}
  </View>;
}

const styles = StyleSheet.create({
  shell: { marginTop: 16, gap: 18 },
  overline: { fontSize: 10, letterSpacing: 0.6, fontWeight: "700" },
  lesson: { borderWidth: 1, borderRadius: 16, padding: 24, gap: 16 },
  number: { fontSize: 10, fontWeight: "800", letterSpacing: 1.1 },
  title: { fontSize: 24, fontWeight: "800", marginBottom: 3 },
  paragraph: { fontSize: 16, lineHeight: 27, maxWidth: 980 },
  emphasis: { fontWeight: "800" },
  illustration: { borderWidth: 1, borderRadius: 16, overflow: "hidden", marginVertical: 10, alignItems: "center", paddingTop: 8, backgroundColor: "#ffffff" },
  illustrationImage: { width: "100%", maxWidth: 960, aspectRatio: 4 / 3, backgroundColor: "#ffffff", alignSelf: "center" },
  caption: { fontSize: 12, lineHeight: 18, paddingHorizontal: 12, paddingVertical: 9 },
  idea: { borderRadius: 10, padding: 14, gap: 7 },
  ideaHeader: { flexDirection: "row", gap: 8, alignItems: "center" },
  ideaLabel: { fontSize: 10, fontWeight: "800", letterSpacing: 0.7 },
});
